import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridCellKind, type EditableGridCell, type GridCell } from '@glideapps/glide-data-grid';
import type { InvestorSummary } from '@nemo/api';
import { throughWords } from '@nemo/core/model';
import { chipsCell } from './grid/cells/index.js';
import { csvOf, orderedColumns, type GridColumnDef } from './grid/columns.js';
import { download, today } from './grid/csv.js';
import { DataGrid } from './grid/DataGrid.js';
import { DensityToggle } from './grid/DensityToggle.js';
import { useGridPreferences } from './grid/useGridPreferences.js';
import { FundPaneHost } from './pane/FundPaneHost.js';
import { roleClassLabel } from './people/roleClass.js';
import { useTRPC } from './trpc.js';
import { formatCount } from './ui/format.js';
import { useToast } from './ui/Toast.js';
import { invalidateFundRows } from './grid/invalidateFundRows.js';

const SEARCH_DEBOUNCE_MS = 300;
const EXPORT_PAGE = 1000;
const PREFERENCES_KEY = 'nemo.grid.investors.v1';

function knownByLabel(edge: InvestorSummary['knownBy'][number]): string {
  return edge.through === null
    ? edge.memberName
    : `${edge.memberName} (${throughWords(edge.through)})`;
}

/** The cells a click opens the person from; the rest are for editing. */
const OPENS_FROM: ReadonlySet<string> = new Set(['fundName', 'attributedRounds', 'knownBy']);

type InvestorEdit =
  | { column: 'name'; value: string }
  | { column: 'title'; value: string | null }
  | { column: 'linkedinUrl'; value: string | null }
  | { column: 'hasLeft'; value: boolean }
  | { column: 'notes'; value: string | null };

interface InvestorColumnDef extends GridColumnDef<InvestorSummary> {
  readonly commit?: (cell: EditableGridCell) => InvestorEdit | null;
}

function text(value: string, editable: boolean): GridCell {
  return {
    kind: GridCellKind.Text,
    data: value,
    displayData: value,
    allowOverlay: editable,
    readonly: !editable,
  };
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

const COLUMNS: readonly InvestorColumnDef[] = [
  {
    id: 'fundName',
    title: 'Fund',
    width: 200,
    cell: (investor) => text(investor.fundName, false),
    text: (investor) => investor.fundName,
  },
  {
    id: 'name',
    editsOnEnter: true,
    title: 'Name',
    width: 180,
    cell: (investor) => text(investor.name, true),
    text: (investor) => investor.name,
    commit: (cell) => {
      if (cell.kind !== GridCellKind.Text || cell.data.trim() === '') return null;
      return { column: 'name', value: cell.data.trim() };
    },
  },
  {
    id: 'title',
    editsOnEnter: true,
    title: 'Title',
    width: 180,
    cell: (investor) => text(investor.title ?? '', true),
    text: (investor) => investor.title ?? '',
    commit: (cell) =>
      cell.kind === GridCellKind.Text ? { column: 'title', value: blankToNull(cell.data) } : null,
  },
  {
    // Read off the title (rules, then a model for odd ones): typing a title
    // reclassifies it. Read-only here for that reason.
    id: 'roleClass',
    title: 'Role',
    width: 100,
    cell: (investor) => text(roleClassLabel(investor.roleClass), false),
    text: (investor) => roleClassLabel(investor.roleClass),
  },
  {
    id: 'linkedinUrl',
    editsOnEnter: true,
    title: 'LinkedIn',
    width: 220,
    cell: (investor) => ({
      kind: GridCellKind.Uri,
      data: investor.linkedinUrl ?? '',
      allowOverlay: true,
      hoverEffect: true,
    }),
    text: (investor) => investor.linkedinUrl ?? '',
    commit: (cell) =>
      cell.kind === GridCellKind.Uri
        ? { column: 'linkedinUrl', value: blankToNull(cell.data) }
        : null,
  },
  {
    id: 'hasLeft',
    title: 'Left?',
    width: 70,
    cell: (investor) => ({
      kind: GridCellKind.Boolean,
      data: investor.hasLeft,
      allowOverlay: false,
    }),
    text: (investor) => (investor.hasLeft ? 'yes' : ''),
    commit: (cell) =>
      cell.kind === GridCellKind.Boolean && typeof cell.data === 'boolean'
        ? { column: 'hasLeft', value: cell.data }
        : null,
  },
  {
    // Rounds this person is named as having done, by the round's own data or
    // its press release. Read-only: it is counted, not typed.
    id: 'attributedRounds',
    title: 'Rounds',
    width: 80,
    cell: (investor) => ({
      kind: GridCellKind.Number,
      data: investor.attributedRounds,
      displayData: investor.attributedRounds === 0 ? '' : String(investor.attributedRounds),
      allowOverlay: false,
      readonly: true,
    }),
    text: (investor) => (investor.attributedRounds === 0 ? '' : String(investor.attributedRounds)),
  },
  {
    id: 'knownBy',
    title: 'Known by',
    width: 240,
    cell: (investor) =>
      chipsCell(investor.knownBy.map((edge) => ({ label: knownByLabel(edge), tone: edge.warmth }))),
    text: (investor) => investor.knownBy.map(knownByLabel).join(', '),
  },
  {
    id: 'notes',
    editsOnEnter: true,
    title: 'Notes',
    width: 300,
    cell: (investor) => text(investor.notes ?? '', true),
    text: (investor) => investor.notes ?? '',
    commit: (cell) =>
      cell.kind === GridCellKind.Text ? { column: 'notes', value: blankToNull(cell.data) } : null,
  },
];

export function InvestorsPage(): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  // The person whose fund the pane is showing, opened at them.
  const [open, setOpen] = useState<InvestorSummary | null>(null);
  const preferences = useGridPreferences(PREFERENCES_KEY);
  const columns = useMemo(() => orderedColumns(COLUMNS, preferences), [preferences]);

  // The search is the api's (a person's name or their fund's), so a keystroke
  // is not a request until typing pauses.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const filter = search === '' ? {} : { text: search };
  const investors = useInfiniteQuery(
    trpc.investors.list.infiniteQueryOptions(filter, {
      getNextPageParam: (page) => page.nextCursor,
    }),
  );

  // Every cached variant of the people list is patched, not just this grid's:
  // the grid's is paged (`pages[].rows[]`), a fund pane's is one page.
  const update = useMutation(
    trpc.investors.update.mutationOptions({
      onMutate: async (edit) => {
        const filter = trpc.investors.list.pathFilter();
        await queryClient.cancelQueries(filter);
        const snapshot = queryClient.getQueriesData(filter);
        const { id, column, value } = edit;
        queryClient.setQueriesData(filter, (cached: unknown) =>
          patchCachedPeople(cached, id, { [column]: value }),
        );
        return { snapshot };
      },
      onError: (error, _edit, context) => {
        for (const [key, data] of context?.snapshot ?? []) queryClient.setQueryData(key, data);
        toast(error.message);
      },
      onSettled: () => {
        // Who knows a fund and the CRM split both read has_left.
        void queryClient.invalidateQueries(trpc.investors.list.pathFilter());
        invalidateFundRows(queryClient, trpc);
      },
    }),
  );

  const rows: readonly InvestorSummary[] = (investors.data?.pages ?? []).flatMap(
    (page) => page.rows,
  );
  const total = investors.data?.pages[0]?.total ?? 0;

  // The export is everything the search matches, not what has been scrolled
  // into view, so it walks the api's pages itself.
  async function exportCsv(): Promise<void> {
    setExporting(true);
    try {
      const all: InvestorSummary[] = [];
      let cursor: string | undefined;
      do {
        const page = await queryClient.fetchQuery(
          trpc.investors.list.queryOptions({
            ...filter,
            limit: EXPORT_PAGE,
            ...(cursor === undefined ? {} : { cursor }),
          }),
        );
        all.push(...page.rows);
        cursor = page.nextCursor ?? undefined;
      } while (cursor !== undefined);
      download(`investors-${today()}.csv`, csvOf(all, columns, undefined));
    } catch (error) {
      toast(
        `Could not export the people: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setExporting(false);
    }
  }

  const onCellEdited = useCallback(
    (investor: InvestorSummary, column: InvestorColumnDef, value: EditableGridCell): void => {
      if (column.commit === undefined) return;
      const edit = column.commit(value);
      if (edit === null) {
        toast(`That is not a value ${column.title} can hold.`);
        return;
      }
      update.mutate({ id: investor.id, ...edit });
    },
    [toast, update],
  );

  const closePane = useCallback(() => setOpen(null), []);

  if (investors.isError) {
    return <p className="subtle">Could not load the people: {investors.error.message}</p>;
  }

  return (
    <div className="page">
      <div className="toolbar">
        <strong className="count">
          {investors.isPending ? 'Loading the people…' : `${formatCount(total)} people`}
        </strong>
        <input
          className="search"
          placeholder="Name or fund…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <span className="spacer" />
        <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
        <button
          className="button primary"
          type="button"
          disabled={exporting}
          onClick={() => void exportCsv()}
        >
          <Download aria-hidden="true" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        context={undefined}
        rowId={(investor) => investor.id}
        preferences={preferences}
        paging={{
          hasMore: investors.hasNextPage,
          loading: investors.isFetchingNextPage,
          onReachEnd: () => void investors.fetchNextPage(),
        }}
        onCellEdited={onCellEdited}
        open={{
          openId: open?.id ?? null,
          onOpen: (investor) => setOpen(investor),
          onClose: closePane,
          opensFrom: (columnId) => OPENS_FROM.has(columnId),
          chevronTip:
            'Open their fund at them — click here, their fund, or press Enter on the row.',
        }}
        aside={(step) =>
          open !== null && (
            <FundPaneHost fundId={open.fundId} person={open.id} onClose={closePane} onStep={step} />
          )
        }
      />
    </div>
  );
}

type CachedPeoplePage = { rows: InvestorSummary[] };

function isPeoplePage(value: unknown): value is CachedPeoplePage {
  return (
    typeof value === 'object' && value !== null && 'rows' in value && Array.isArray(value.rows)
  );
}

function patchPage(page: CachedPeoplePage, id: string, patch: Partial<InvestorSummary>) {
  return { ...page, rows: page.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)) };
}

/** An infinite query caches `{ pages, pageParams }`; a plain one caches the page itself. */
function patchCachedPeople(cached: unknown, id: string, patch: Partial<InvestorSummary>): unknown {
  if (isPeoplePage(cached)) return patchPage(cached, id, patch);
  if (
    typeof cached === 'object' &&
    cached !== null &&
    'pages' in cached &&
    Array.isArray(cached.pages)
  ) {
    return {
      ...cached,
      pages: cached.pages.map((page: unknown) =>
        isPeoplePage(page) ? patchPage(page, id, patch) : page,
      ),
    };
  }
  return cached;
}
